import React, { useState, useRef } from 'react';
import { FiUpload, FiTrash2, FiX } from 'react-icons/fi';
import axios from 'axios';

const MAX_STORE_IMAGES_PER_UPLOAD = 5;
const isVideo = (url = '') => /\.(mp4|mov|webm|m4v|avi)(\?|$)/i.test(url);

// Gallery items must be linked to a vendor service (same rule as the vendor app).
// Store images are a plain list on the vendor record.
const VendorGalleryTab = ({ vendorId, vendorData, storeImages = [], onGalleryChange, onStoreImagesChange }) => {
  const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api`;
  const token = localStorage.getItem('token');
  const authHeader = { Authorization: `Bearer ${token}` };

  const gallery = vendorData.gallery || [];
  const services = vendorData.services || [];

  // Sub-services are shown as "Parent › Child" so they can be told apart
  const serviceOptions = services
    .map(s => {
      const parent = s.parentServiceId && services.find(p => p._id === String(s.parentServiceId));
      return { _id: s._id, label: parent ? `${parent.name} › ${s.name}` : s.name };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  // Gallery upload
  const [galleryServiceId, setGalleryServiceId] = useState('');
  const [galleryFile, setGalleryFile] = useState(null);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [deletingGalleryId, setDeletingGalleryId] = useState(null);
  const galleryInputRef = useRef(null);

  // Store images upload
  const [storeFiles, setStoreFiles] = useState([]);
  const [storeSaving, setStoreSaving] = useState(false);
  const storeInputRef = useRef(null);

  const resetGalleryForm = () => {
    setGalleryFile(null);
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  const handleGalleryUpload = async () => {
    if (!galleryServiceId) return alert('Select a service for this gallery item');
    if (!galleryFile) return alert('Choose an image or video to upload');

    const formData = new FormData();
    formData.append('vendorId', vendorId);
    formData.append('vendorServiceId', galleryServiceId);
    formData.append('file', galleryFile);

    setGalleryUploading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/vendor-gallery`, formData, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        onGalleryChange([response.data.data, ...gallery]);
        resetGalleryForm();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to upload gallery item');
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleGalleryDelete = async (item) => {
    if (!window.confirm('Delete this gallery item? This removes it from the vendor\'s profile.')) return;
    setDeletingGalleryId(item._id);
    try {
      await axios.delete(`${API_BASE_URL}/admin/vendor-gallery/${item._id}`, { headers: authHeader });
      onGalleryChange(gallery.filter(g => g._id !== item._id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete gallery item');
    } finally {
      setDeletingGalleryId(null);
    }
  };

  // Uses the existing vendor update endpoint: keep-list + new files
  const saveStoreImages = async (keepImages, newFiles) => {
    const formData = new FormData();
    formData.append('existing_store_images', JSON.stringify(keepImages.map(img => img.url)));
    newFiles.forEach(file => formData.append('store_images', file));

    setStoreSaving(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/vendor/${vendorId}`, formData, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        onStoreImagesChange(response.data.data.store_images || []);
        return true;
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update store images');
    } finally {
      setStoreSaving(false);
    }
    return false;
  };

  const handleStoreFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > MAX_STORE_IMAGES_PER_UPLOAD) {
      alert(`You can upload up to ${MAX_STORE_IMAGES_PER_UPLOAD} store images at a time`);
      e.target.value = '';
      return;
    }
    setStoreFiles(files);
  };

  const handleStoreUpload = async () => {
    if (storeFiles.length === 0) return;
    const ok = await saveStoreImages(storeImages, storeFiles);
    if (ok) {
      setStoreFiles([]);
      if (storeInputRef.current) storeInputRef.current.value = '';
    }
  };

  const handleStoreDelete = async (image) => {
    if (!window.confirm('Delete this store image?')) return;
    await saveStoreImages(storeImages.filter(img => img.url !== image.url), []);
  };

  const renderMedia = (url, alt) =>
    isVideo(url) ? (
      <video src={url} className="w-full h-48 object-cover" muted controls preload="metadata" />
    ) : (
      <img src={url} alt={alt} className="w-full h-48 object-cover" />
    );

  return (
    <div>
      {/* Gallery */}
      <h3 className="text-xl font-bold mb-4 text-gray-800">Gallery</h3>

      <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
          <select
            value={galleryServiceId}
            onChange={e => setGalleryServiceId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            disabled={galleryUploading}
          >
            <option value="">{serviceOptions.length ? 'Select a service' : 'No services — add one first'}</option>
            {serviceOptions.map(s => (
              <option key={s._id} value={s._id}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Image or video</label>
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*,video/*"
            onChange={e => setGalleryFile(e.target.files?.[0] || null)}
            className="w-full text-sm"
            disabled={galleryUploading}
          />
        </div>
        <button
          type="button"
          onClick={handleGalleryUpload}
          disabled={galleryUploading || !galleryServiceId || !galleryFile}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium disabled:opacity-50"
        >
          <FiUpload className="w-4 h-4" />
          {galleryUploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>

      {gallery.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {gallery.map((item, index) => (
            <div key={item._id || index} className="group relative overflow-hidden rounded-lg border border-gray-100">
              {renderMedia(item.file, `Gallery item ${index + 1}`)}
              <div className="px-3 py-2 text-xs text-gray-600 truncate">
                {item.vendorServiceId?.name || 'Service photo'}
              </div>
              <button
                type="button"
                onClick={() => handleGalleryDelete(item)}
                disabled={deletingGalleryId === item._id}
                className="absolute top-2 right-2 p-2 bg-white/90 text-red-600 rounded-full shadow opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity disabled:opacity-50"
                aria-label="Delete gallery item"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-10">
          <p className="text-gray-500 italic">No gallery images found</p>
          <p className="text-sm text-gray-500 mt-2">Upload one above, linked to a service.</p>
        </div>
      )}

      {/* Store Images */}
      <div className="mt-10">
        <h3 className="text-xl font-bold mb-4 text-gray-800">Store Images</h3>

        <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
          <input
            ref={storeInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleStoreFilesSelected}
            className="flex-1 min-w-[200px] text-sm"
            disabled={storeSaving}
          />
          {storeFiles.length > 0 && (
            <button
              type="button"
              onClick={() => { setStoreFiles([]); if (storeInputRef.current) storeInputRef.current.value = ''; }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
              disabled={storeSaving}
            >
              <FiX className="w-4 h-4" /> Clear
            </button>
          )}
          <button
            type="button"
            onClick={handleStoreUpload}
            disabled={storeSaving || storeFiles.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium disabled:opacity-50"
          >
            <FiUpload className="w-4 h-4" />
            {storeSaving ? 'Saving...' : `Upload${storeFiles.length ? ` (${storeFiles.length})` : ''}`}
          </button>
        </div>

        {storeImages.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {storeImages.map((image, index) => (
              <div key={image._id || image.url} className="relative group overflow-hidden rounded-lg">
                <img src={image.url} alt={`Store ${index + 1}`} className="w-full h-48 object-cover" />
                <button
                  type="button"
                  onClick={() => handleStoreDelete(image)}
                  disabled={storeSaving}
                  className="absolute top-2 right-2 p-2 bg-white/90 text-red-600 rounded-full shadow opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity disabled:opacity-50"
                  aria-label="Delete store image"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 italic">No store images uploaded</p>
        )}
      </div>
    </div>
  );
};

export default VendorGalleryTab;
